import { FormInstance, useForm, useWatch } from 'antd/lib/form/Form'
import { ProjectFeatureFlags } from 'HTTPClients/projects.client'
import { useCallback, useMemo, useState } from 'react'
import { GenerateResponseForm } from 'Tasks/CustomTask/UI/common/GenerateResponseForm/GenerateResponseForm'
import { HumanResponse } from 'Tasks/CustomTask/UI/common/HumanResponse/HumanResponse'
import { PreviewResponse } from 'Tasks/CustomTask/UI/common/PreviewResponse/PreviewResponse'
import { TurnPrimaryResponse } from 'Tasks/CustomTask/UI/common/TurnResponse/TurnPrimaryResponse/TurnPrimaryResponse'
import { TurnStepResponse } from 'Tasks/CustomTask/UI/common/TurnResponse/TurnStepResponse/TurnStepResponse'
import {
    ContainerDataType,
    ContainerType,
    CustomDataType,
    GenerateResponseFormType,
    HumanResponseFormType,
    ManualResponseFormType,
    PrimaryResponseFormType,
    TurnData,
    TurnResponseFormType,
} from 'Types/Tasks.types'
import { ManualResponseForm } from '../common/ManualResponseForm/ManualResponseForm'
import { CustomForm } from 'Types/CustomTask.types'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { LiveFeedbackContextType } from 'Types/LiveFeedback.types'

type CreationModePropsType = {
    featureFlags?: ProjectFeatureFlags['feature_flags']
    modelResponseExtraInfo: {
        totalModelResponsesOnPromptRevision: number
        modelResponseFeedbackCode: { value: number; label: string }[]
        modelResponse: string
    }
    turnData: () =>
        | {
              data: TurnData
              turnNumber: number
              retryCount: number
          }
        | undefined
    onSubmitData: (
        type: 'TURN_ANNOTATION' | 'CORRECT_ANSWER',
        firstIncorrectStep?: number | null
    ) => void
    onSubmitGenerateResponseForm: () => void
    onRetryTurn: () => void
    setForms: (forms: CustomForm) => void
    attributesUI?: UpdatableReactElement
    installedPlugins?: IPlugin[]
    question: string
    setIsLiveFeedbackChecked: () => void
    outputStartState?: CustomDataType
    liveFeedbackContext: LiveFeedbackContextType
    areAttributesValid: () => Promise<boolean>
    onContainerUpdate?: (container: ContainerDataType<ContainerType.Custom, CustomDataType>) => void
}

export const CreationMode = (props: CreationModePropsType) => {
    const [generateResponseForm] = useForm<GenerateResponseFormType>()
    const [manualResponseForm] = useForm<ManualResponseFormType>()
    const [primaryResponseForm] = useForm<PrimaryResponseFormType>()
    const [turnResponseForm] = useForm<TurnResponseFormType>()
    const [humanResponseForm] = useForm<HumanResponseFormType>()

    const [turnPrimaryResponseCompleted, setTurnPrimaryResponseCompleted] = useState<boolean>(false)
    const [firstIncorrectStep, setFirstIncorrectStep] = useState<number | null>(null)

    const enableTurnAnnotations: boolean = !!props.featureFlags?.turn_annotation_enabled
    const acceptPromptOptions: boolean = !!props.featureFlags?.stem_project

    const onSubmitTurnData = useCallback(async () => {
        const isValidData = await turnResponseForm.validateFields()
        if (!isValidData) {
            return
        }

        props.onSubmitData('TURN_ANNOTATION', firstIncorrectStep)
        setTurnPrimaryResponseCompleted(false)
        setFirstIncorrectStep(null)

        primaryResponseForm.resetFields()
        turnResponseForm.resetFields()
    }, [firstIncorrectStep, primaryResponseForm, props, turnResponseForm])

    const onSubmitHumanResponse = useCallback(() => {
        onSubmitTurnData()

        props.onSubmitData('CORRECT_ANSWER')
    }, [onSubmitTurnData, props])

    const onClickRetry = useCallback(() => {
        setTurnPrimaryResponseCompleted(false)
        setFirstIncorrectStep(null)

        primaryResponseForm.resetFields()
        turnResponseForm.resetFields()

        props.onRetryTurn()
    }, [primaryResponseForm, props, turnResponseForm])

    const generateResponseContent = useMemo(() => {
        if (props.turnData()) {
            return null
        } else {
            if (props.featureFlags?.lmf_project || props.featureFlags?.ots_project) {
                return (
                    <ManualResponseForm
                        setForms={props.setForms}
                        form={manualResponseForm}
                        onSubmit={() => props.onSubmitGenerateResponseForm()}
                        modelResponseExtraInfo={props.modelResponseExtraInfo}
                        attributesUI={props.attributesUI}
                        featureFlags={props.featureFlags}
                        promptAnnotations={
                            props.outputStartState?.response?.main_info?.value?.prompt_annotation
                        }
                        installedPlugins={props.installedPlugins}
                        question={props.question}
                        setIsLiveFeedbackChecked={props.setIsLiveFeedbackChecked}
                        isLMFProject={!!props.featureFlags?.lmf_project}
                        isStemProject={!!props.featureFlags?.stem_project}
                        liveFeedbackContext={props.liveFeedbackContext}
                        areAttributesValid={props.areAttributesValid}
                    />
                )
            }
            return (
                <GenerateResponseForm
                    setForms={props.setForms}
                    form={generateResponseForm}
                    onSubmit={() => props.onSubmitGenerateResponseForm()}
                    enableTurnAnnotations={enableTurnAnnotations}
                    hasPromptOptions={acceptPromptOptions}
                    modelResponseExtraInfo={props.modelResponseExtraInfo}
                    attributesUI={props.attributesUI}
                    isLMFProject={!!props.featureFlags?.lmf_project}
                    isStemProject={!!props.featureFlags?.stem_project}
                    installedPlugins={props.installedPlugins}
                    question={props.question}
                    setIsLiveFeedbackChecked={props.setIsLiveFeedbackChecked}
                    promptAnnotations={
                        props.outputStartState?.response?.main_info?.value?.prompt_annotation
                    }
                    liveFeedbackContext={props.liveFeedbackContext}
                    areAttributesValid={props.areAttributesValid}
                />
            )
        }
    }, [
        props,
        generateResponseForm,
        enableTurnAnnotations,
        acceptPromptOptions,
        manualResponseForm,
    ])

    const manualSteps = useWatch<HumanResponseFormType['values'] | undefined>(
        'values',
        humanResponseForm
    )

    const validateTurnResponseForm: () => Promise<boolean> = useCallback(async () => {
        return !!(await turnResponseForm.validateFields())
    }, [turnResponseForm])

    const turnContent = useMemo(() => {
        const content: JSX.Element[] = []

        const turnData = props.turnData()
        const humanResponseNeeded = turnData?.turnNumber === 5

        if (turnData) {
            content.push(
                <PreviewResponse
                    turn={turnData?.turnNumber ?? 0}
                    retryCount={turnData?.retryCount ?? 0}
                    responseValue={
                        turnData?.data.llm_response.replace(/(?<!\n\n)## Step/g, '\n\n ## Step') ??
                        ''
                    }
                    onClickRetry={onClickRetry}
                />
            )
            content.push(
                <TurnPrimaryResponse
                    setPrimaryResponseForm={(form: FormInstance<PrimaryResponseFormType>) => {
                        props.setForms({
                            primaryResponseForms: [form],
                        })
                    }}
                    form={primaryResponseForm}
                    onClickProceed={() => setTurnPrimaryResponseCompleted(true)}
                    disabled={turnPrimaryResponseCompleted}
                />
            )

            if (turnPrimaryResponseCompleted) {
                content.push(
                    <TurnStepResponse
                        setTurnStepResponseForm={(form: FormInstance<TurnResponseFormType>) => {
                            props.setForms({
                                turnResponseForms: [form],
                            })
                        }}
                        firstIncorrectStep={firstIncorrectStep}
                        setFirstIncorrectStep={setFirstIncorrectStep}
                        steps={turnData.data.steps}
                        isCorrectResponse={primaryResponseForm.getFieldValue('isCorrect')}
                        submitTurnData={onSubmitTurnData}
                        humanResponseNeeded={humanResponseNeeded}
                        turn={turnData.turnNumber}
                        retryCount={turnData.retryCount}
                        hasManualSteps={!!manualSteps?.length}
                        form={turnResponseForm}
                    />
                )
            }

            if (
                humanResponseNeeded &&
                turnPrimaryResponseCompleted &&
                !primaryResponseForm.getFieldValue('isCorrect')
            ) {
                content.push(
                    <HumanResponse
                        setForms={props.setForms}
                        form={humanResponseForm}
                        onSubmitHumanResponse={onSubmitHumanResponse}
                        disableAddStep={firstIncorrectStep === null}
                        validateTurnResponseForm={validateTurnResponseForm}
                    />
                )
            }

            return content
        }

        return null
    }, [
        validateTurnResponseForm,
        firstIncorrectStep,
        humanResponseForm,
        onClickRetry,
        onSubmitHumanResponse,
        onSubmitTurnData,
        primaryResponseForm,
        props,
        turnPrimaryResponseCompleted,
        turnResponseForm,
        manualSteps,
    ])

    return (
        <>
            {generateResponseContent}
            {turnContent}
        </>
    )
}
